import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtMoney, fmtQty, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

export default function Receipts() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [pos, setPos] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [poNumber, setPoNumber] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [location, setLocation] = useState("");
  const [quarantine, setQuarantine] = useState(false);
  const [qtys, setQtys] = useState({});
  const [serials, setSerials] = useState({});
  const [lineLocs, setLineLocs] = useState({});
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    api.get("/receipts").then((r) => setReceipts(r.data));
    api.get("/purchase-orders").then((r) => setPos(r.data.filter((p) => ["open", "partial"].includes(p.status))));
  }, []);

  useEffect(() => {
    load();
    api.get("/warehouses").then((r) => setWarehouses(r.data));
    api.get("/locations").then((r) => setLocations(r.data));
    api.get("/items").then((r) => setItems(r.data));
  }, [load]);

  const po = pos.find((p) => p.number === poNumber);
  const isSerial = (num) => items.find((i) => i.item_number === num)?.serial_controlled;

  const post = async () => {
    try {
      const lines = Object.entries(qtys).filter(([, v]) => Number(v) > 0)
        .map(([item_number, qty]) => ({
          item_number, qty: Number(qty),
          location: lineLocs[item_number] || "",
          serials: isSerial(item_number) ? (serials[item_number] || "").split(",").map((s) => s.trim()).filter(Boolean) : [],
        }));
      await api.post("/receipts", { po_number: poNumber, warehouse, location, quarantine, lines });
      toast.success(t("rc.posted"));
      setPoNumber(""); setWarehouse(""); setLocation(""); setQuarantine(false); setQtys({}); setSerials({}); setLineLocs({});
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="receipts-page">
      <PageHeader title={t("rc.title")} subtitle={t("rc.subtitle")} />

      {can("admin", "warehouse") && (
        <div className="border bg-card p-4 mb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">{t("rc.new")}</div>
          <div className="flex gap-3 flex-wrap mb-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rc.selectPo")}</Label>
              <Select value={poNumber} onValueChange={(v) => { setPoNumber(v); setQtys({}); }}>
                <SelectTrigger className="rounded-none h-8 text-xs w-72" data-testid="receipt-po-select"><SelectValue /></SelectTrigger>
                <SelectContent>{pos.map((p) => <SelectItem key={p.id} value={p.number}>{p.number} — {p.supplier_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.warehouse")}</Label>
              <Select value={warehouse} onValueChange={(v) => { setWarehouse(v); setLocation(""); }}>
                <SelectTrigger className="rounded-none h-8 text-xs w-64" data-testid="receipt-warehouse-select"><SelectValue /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.code}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rc.location")}</Label>
              <Select value={location || "none"} onValueChange={(v) => setLocation(v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-none h-8 text-xs w-48" data-testid="receipt-location-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("loc.none")}</SelectItem>
                  {locations.filter((l) => l.warehouse === warehouse).map((l) => <SelectItem key={l.id} value={l.code}>{l.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex flex-col justify-end pb-1">
              <div className="flex items-center gap-2">
                <Switch checked={quarantine} onCheckedChange={setQuarantine} data-testid="receipt-quarantine-switch" />
                <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rc.quarantine")}</Label>
              </div>
            </div>
          </div>
          {po && (
            <>
              <div className="tbl-wrap mb-3">
                <table className="tbl">
                  <thead><tr>
                    <th>{t("common.item")}</th><th>{t("common.description")}</th>
                    <th className="num">{t("common.qty")}</th><th className="num">{t("po.receivedQty")}</th>
                    <th className="num">{t("rc.remaining")}</th><th className="num">{t("rc.toReceive")}</th>
                    <th>{t("rc.lineLocation")}</th>
                  </tr></thead>
                  <tbody>
                    {po.lines.map((l) => {
                      const remaining = l.qty - l.received_qty;
                      const serial = isSerial(l.item_number);
                      const item = items.find((i) => i.item_number === l.item_number);
                      return (
                        <tr key={l.item_number}>
                          <td className="font-bold">
                            <div className="flex items-center gap-2">
                              {item?.image && (
                                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/items/${item.id}/image`} alt="" className="w-10 h-10 object-cover border"
                                  data-testid={`receipt-item-image-${l.item_number}`} />
                              )}
                              <span>{l.item_number}{serial && <span className="text-[9px] font-bold text-primary ml-1">SN</span>}</span>
                            </div>
                          </td><td>{l.description}</td>
                          <td className="num">{fmtQty(l.qty)}</td>
                          <td className="num">{fmtQty(l.received_qty)}</td>
                          <td className="num font-bold">{fmtQty(remaining)}</td>
                          <td className="num">
                            <Input type="number" min="0" max={remaining} value={qtys[l.item_number] || ""}
                              onChange={(e) => setQtys({ ...qtys, [l.item_number]: e.target.value })}
                              className="rounded-none h-7 text-xs w-24 inline-block text-right"
                              data-testid={`receipt-qty-${l.item_number}`} disabled={remaining <= 0} />
                            {serial && Number(qtys[l.item_number]) > 0 && (
                              <Input value={serials[l.item_number] || ""} placeholder={t("sn.enter")}
                                onChange={(e) => setSerials({ ...serials, [l.item_number]: e.target.value })}
                                className="rounded-none h-7 text-xs w-64 inline-block ml-2"
                                data-testid={`receipt-serials-${l.item_number}`} />
                            )}
                          </td>
                          <td>
                            <Select value={lineLocs[l.item_number] || "same"} disabled={remaining <= 0}
                              onValueChange={(v) => setLineLocs({ ...lineLocs, [l.item_number]: v === "same" ? "" : v })}>
                              <SelectTrigger className="rounded-none h-7 text-[11px] w-44" data-testid={`receipt-line-location-${l.item_number}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="same">{t("rc.sameLocation")}</SelectItem>
                                {locations.filter((x) => x.warehouse === warehouse).map((x) => <SelectItem key={x.id} value={x.code}>{x.code}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Button onClick={post} disabled={!warehouse || !Object.values(qtys).some((v) => Number(v) > 0)}
                className="rounded-none font-bold text-xs" data-testid="receipt-post-button">{t("rc.post")}</Button>
            </>
          )}
        </div>
      )}

      {receipts.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="receipts-table">
          <table className="tbl">
            <thead><tr>
              <th>{t("common.number")}</th><th>OC</th><th>{t("common.supplier")}</th>
              <th>{t("common.warehouse")}</th><th className="num">{t("common.lines")}</th>
              <th className="num">{t("common.total")}</th><th>{t("rc.receivedBy")}</th><th>{t("common.date")}</th>
            </tr></thead>
            <tbody>
              {receipts.map((r) => (
                <tr key={r.id}>
                  <td className="font-bold">{r.number}</td>
                  <td>{r.po_number}</td>
                  <td>{r.supplier_name}</td>
                  <td>{r.warehouse}</td>
                  <td className="num">{r.lines.length}{r.lines.some((l) => l.location) && <span className="text-[9px] text-muted-foreground block">{[...new Set(r.lines.map((l) => l.location).filter(Boolean))].join(", ")}</span>}</td>
                  <td className="num font-bold">{fmtMoney(r.lines.reduce((s, l) => s + l.qty * l.unit_cost, 0))}</td>
                  <td className="text-muted-foreground">{r.received_by}</td>
                  <td className="text-muted-foreground">{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
